import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import styles from './intake2.module.scss';
import { useTrackingContext } from '../../hooks/useTrackingContext';
import { useAppStoreUrl } from '../../hooks/useAppStoreUrl';
import { TrackingContext } from '../../context/TrackingContext';
import { CLUSTER } from '../../constants';

const BASE_PATH = CLUSTER !== 'dev' ? '/ai-ui' : '';

const QS = [
  {
    type: 'visual selection',
    hed: 'What pulls you in?',
    img: null,
    date: null,
    grid: 'q1',
    opts: [
      { l: 'A', t: 'Dense forest at dusk, fog between the trees' },
      { l: 'B', t: 'Long empty corridor, one flickering light at the end' },
      { l: 'C', t: 'An abandoned church, doors open, pale light through broken windows' },
      { l: 'D', t: 'A coastal cliff at night, waves below, no horizon visible' },
    ],
  },
  {
    type: 'frequency response',
    hed: 'What do you see?',
    img: `${BASE_PATH}/static/images/conjure/intake2/q-2.png`,
    date: '08/03/2003',
    grid: null,
    opts: [
      { l: 'A', t: 'Something large, just beneath the surface' },
      { l: 'B', t: 'A rock formation on the lake bed' },
      { l: 'C', t: 'The sky, reflected and distorted' },
      { l: 'D', t: "Cold water. That's all that is." },
    ],
  },
  {
    type: 'visual selection',
    hed: 'Which holds your attention?',
    img: null,
    date: null,
    grid: 'q3',
    opts: [
      { l: 'A', t: 'Vast open ocean at night, nothing on the horizon' },
      { l: 'B', t: 'A single room, one chair, one window, curtain moving' },
      { l: 'C', t: 'A cave entrance, darkness inside, handprints on the stone walls' },
      { l: 'D', t: 'An empty fairground at 3am, lights still running, no one there' },
    ],
  },
  {
    type: 'frequency response',
    hed: 'What do you see?',
    img: `${BASE_PATH}/static/images/conjure/intake2/q-4.png`,
    date: '05/17/2000',
    grid: null,
    opts: [
      { l: 'A', t: 'Something crouching between the stalks' },
      { l: 'B', t: 'A shadow from something passing overhead' },
      { l: 'C', t: 'A section the corn grew around' },
      { l: 'D', t: 'Nothing. Crops do this.' },
    ],
  },
  {
    type: 'visual selection',
    hed: 'Which holds your attention?',
    img: null,
    date: null,
    grid: 'q5',
    opts: [
      { l: 'A', t: 'A door at the end of a hallway, light coming from underneath' },
      { l: 'B', t: 'A staircase going down, no light at the bottom' },
      { l: 'C', t: 'A trail disappearing into dense woods, something hanging from a branch' },
      { l: 'D', t: 'An old elevator, doors open, going up' },
    ],
  },
  {
    type: 'frequency response',
    hed: 'What do you see?',
    img: `${BASE_PATH}/static/images/conjure/intake2/q-6.png`,
    date: '10/31/2001',
    grid: null,
    opts: [
      { l: 'A', t: 'A figure standing between the trees' },
      { l: 'B', t: 'Mist catching the light from somewhere off-frame' },
      { l: 'C', t: 'Motion blur — something passed through during the exposure' },
      { l: 'D', t: "Nothing. It's just the light." },
    ],
  },
  {
    type: 'frequency response',
    hed: 'What do you see?',
    img: `${BASE_PATH}/static/images/conjure/intake2/q-8.png`,
    date: '05/17/2000',
    grid: null,
    opts: [
      { l: 'A', t: 'Something moving through the atmosphere' },
      { l: 'B', t: 'A rocket or aircraft exhaust catching the light' },
      { l: 'C', t: 'A weather phenomenon — ball lightning, a halo' },
      { l: 'D', t: "I'm not looking at the sky. I'm looking at the city below it." },
    ],
  },
];

const RES = {
  A: {
    h: 'assessment: closed eye',
    m: 'frequency sensitivity — NONE DETECTED | signal blocked | transmission redirected',
    b: '<p>You are, by nature, a person who trusts what can be verified. You make good decisions because you don\'t let things cloud your judgment. That\'s actually rare. But here\'s what\'s also true: <strong>you took this quiz. Something made you click.</strong></p><p>Maybe you\'ve been feeling a little restless, a little more unsettled by the world than you usually are. And if you\'re not, that\'s fine.</p><p>We just ask — <strong>send this assessment to the person in your family</strong> who is more inclined to\u2026question things. You already know who it is. Forward this to them. They\'ve been waiting for something that finally takes them seriously.</p>',
    art: [
      '                              ',
      '      ━━━━━━━━━━━━━━━━━       ',
      '    ╱                   ╲     ',
      '   ╱   ─ ─ ─ ─ ─ ─ ─   ╲    ',
      '  │  ─ ─ ─ ─ ─ ─ ─ ─ ─  │   ',
      '   ╲   ─ ─ ─ ─ ─ ─ ─   ╱    ',
      '    ╲                   ╱     ',
      '      ━━━━━━━━━━━━━━━━━       ',
      '                              ',
      '      ┊ signal blocked ┊      ',
    ],
  },
  B: {
    h: 'assessment: looked away',
    m: 'frequency sensitivity — DETECTED | signal received | you already know what you saw',
    b: '<p>You are someone who <strong>feels things before you can explain them</strong> — and then works very hard to explain them. You\'re perceptive in ways that unsettle you a little. You notice when a room has changed and you feel it in the air.</p><p>You\'ve had moments — specific ones you still remember — where <strong>something happened that you quietly filed away under coincidence</strong> because the alternative required more of you than you were ready to give.</p><p>You\'re not a skeptic. A skeptic wouldn\'t have hesitated at those images the way you did. You\'re something more specific: a person who knows that <strong>learning more is a one-way door</strong>, and you\'re not sure you\'re ready to walk through it. That caution is actually a sign of real intelligence. You understand the weight of what you\'re approaching.</p><p>You\'ve felt it before. You\'ll feel it again. <strong>We\'ll be here when you\'re ready to stop looking away.</strong></p>',
    art: [
      '                              ',
      '         ╭───╮               ',
      '         │ ◉ │  ╲            ',
      '         ╰─┬─╯    ╲         ',
      '           │        ·        ',
      '          ╱│╲      ·         ',
      '         ╱ │ ╲    ·          ',
      '        ╱  │  ╲  ◌           ',
      '           │                 ',
      '          ╱ ╲                ',
      '     ┊ looked away ┊        ',
    ],
  },
  C: {
    h: 'assessment: at the door',
    m: 'frequency sensitivity — ELEVATED | threshold detected | signal building',
    b: '<p>You are someone who <strong>lives with open questions comfortably</strong> — more comfortably than most. You don\'t need resolution. You\'re drawn to the liminal: the moment between sleeping and waking, the pause before someone answers, <strong>the hallway rather than the room at the end of it</strong>. People probably describe you as hard to read, or as someone who asks unusual questions, or as the one who takes conversations somewhere unexpected.</p><p><strong>You believe in more than you\'ll readily admit.</strong> Not out of embarrassment — out of precision. You don\'t want to claim more than you\'ve actually experienced. You\'re waiting for something concrete enough to commit to.</p><p>That\'s exactly what Conjure was built for. Not for people who\'ve already decided — for people like you, who are a bit more rigorous about it. Who want to understand the frequency before tuning in. Who need the community to be made of people who ask real questions, not people who accept anything.</p><p>They\'re in there. People who are exactly as careful as you are — and further along. <strong>The door\'s been open this whole time.</strong></p>',
    art: [
      '      ┌──────┬──────┐      ',
      '      │▓▓▓▓▓▓│      │      ',
      '      │▓▓▓▓▓▓│      │      ',
      '      │▓▓▓▓▓▓│  ░   │      ',
      '      │▓▓▓▓▓▓│      │      ',
      '      │▓▓▓▓▓▓│ ◌    │      ',
      '      │▓▓▓▓▓▓│      │      ',
      '      ├──────┘      │      ',
      '      │    ▫▫▫▫▫    │      ',
      '      └─────────────┘      ',
      '    ┊ the door is open ┊   ',
    ],
  },
  D: {
    h: 'assessment: already knows',
    m: 'frequency sensitivity — HIGH | anomaly detection — ACTIVE | summons issued',
    b: '<p>You are someone who has always processed the world at a slightly different register than the people around you. You notice what others don\'t — not because you\'re looking for it, but because <strong>it finds you first</strong>. Details in the background. Patterns that aren\'t supposed to be there. The thing in a photograph that everyone else scrolls past\u2026you actually notice right away.</p><p>You\'ve learned to be quiet about it. Not because you doubt yourself — you stopped doubting yourself a while ago — but because you\'ve seen how people react, and it\'s easier to say nothing. You\'ve developed a very particular kind of loneliness: <strong>the loneliness of knowing something and having nowhere to put it.</strong></p><p>You have a finely tuned sense for what\'s real and what\'s performance, and that sense is precisely what makes what you\'ve seen harder to dismiss.</p><p>Conjure captures the frequency you\'ve already been detecting with nothing but your own perception. <strong>What you\'ve been seeing your whole life, without a name for it, without a community — this is where it lives.</strong> But this time, you won\'t be alone.</p><p><strong>Your first summons has been waiting.</strong></p>',
    art: [
      '                              ',
      '        ╭━━━━━━━━━╮          ',
      '      ╱               ╲      ',
      '    ╱    ╭─────────╮    ╲    ',
      '   │    │     ◉     │    │   ',
      '    ╲    ╰─────────╯    ╱    ',
      '      ╲               ╱      ',
      '        ╰━━━━━━━━━╯          ',
      '                              ',
      '     ┊ summons issued ┊      ',
    ],
  },
};

const GRID_IMAGES = {
  q1: {
    A: `${BASE_PATH}/static/images/conjure/intake2/q1-grid/Q1A.png`,
    B: `${BASE_PATH}/static/images/conjure/intake2/q1-grid/Q1B.png`,
    C: `${BASE_PATH}/static/images/conjure/intake2/q1-grid/Q1C.png`,
    D: `${BASE_PATH}/static/images/conjure/intake2/q1-grid/Q1D.jpg`,
  },
  q3: {
    A: `${BASE_PATH}/static/images/conjure/intake2/q3-grid/Q3A.jpg`,
    B: `${BASE_PATH}/static/images/conjure/intake2/q3-grid/Q3B.jpg`,
    C: `${BASE_PATH}/static/images/conjure/intake2/q3-grid/Q3C.jpg`,
    D: `${BASE_PATH}/static/images/conjure/intake2/q3-grid/Q3D.jpg`,
  },
  q5: {
    A: `${BASE_PATH}/static/images/conjure/intake2/q5-grid/Q5A.jpg`,
    B: `${BASE_PATH}/static/images/conjure/intake2/q5-grid/Q5B.jpg`,
    C: `${BASE_PATH}/static/images/conjure/intake2/q5-grid/Q5C.jpg`,
    D: `${BASE_PATH}/static/images/conjure/intake2/q5-grid/Q5D.jpg`,
  },
};

function Intake2Quiz() {
  const [section, setSection] = useState('intro'); // intro | quiz | result
  const [cur, setCur] = useState(0);
  const [answers, setAnswers] = useState({});
  const [seekers, setSeekers] = useState(null);
  const [postedHours, setPostedHours] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const seekersInitialized = useRef(false);

  const {
    trackContentAction,
    trackExternalLink,
    trackPageview,
    attachTimeSpentHandler,
  } = useTrackingContext({ isConjure: true });

  const appStoreUrl = useAppStoreUrl('website-intake-2');

  // Pageview + time spent on mount
  useEffect(() => {
    trackPageview({ context_page_type: 'utility', context_page_id: 'intake2', destination: 'conjure', page_edition: 'en-us' });
    attachTimeSpentHandler({
      unit_type: 'component',
      unit_name: 'conjure_intake_2',
      context_page_type: 'utility',
      context_page_id: 'intake2',
      destination: 'conjure',
      page_edition: 'en-us',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!seekersInitialized.current) {
      setSeekers(Math.floor(Math.random() * (3299 - 37 + 1)) + 37);
      setPostedHours(Math.floor(Math.random() * 8) + 2);
      setRecordId(Math.floor(Math.random() * (7938294012 - 7738294012 + 1)) + 7738294012);
      seekersInitialized.current = true;
    }
    const interval = setInterval(() => {
      const delta = Math.floor(Math.random() * 18) + 3; // 3-20
      const direction = Math.random() < 0.5 ? -1 : 1;
      setSeekers((prev) => Math.max(37, Math.min(3299, prev + delta * direction)));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const q = QS[cur];
  const pct = Math.round(((cur + 1) / QS.length) * 100);
  const sel = answers[`q${cur}`];
  const hasAns = !!sel;
  const isLast = cur === QS.length - 1;

  const startQuiz = useCallback(() => {
    trackContentAction({
      unit_type: 'component',
      unit_name: 'conjure_intake_2',
      subunit_type: 'component',
      subunit_name: 'quiz_entry',
      item_type: 'button',
      item_name: 'begin_assessment',
      action_type: 'select',
      action_value: 'start_quiz',
    });
    setSection('quiz');
    window.scrollTo(0, 0);
  }, [trackContentAction]);

  const retake = useCallback(() => {
    trackContentAction({
      unit_type: 'component',
      unit_name: 'conjure_intake_2',
      subunit_type: 'component',
      subunit_name: 'quiz_result',
      item_type: 'button',
      item_name: 'retake_assessment',
      action_type: 'select',
      action_value: 'quiz_retake',
    });
    setCur(0);
    setAnswers({});
    setSection('intro');
    window.scrollTo(0, 0);
  }, [trackContentAction]);

  const pick = useCallback(
    (letter) => {
      trackContentAction({
        unit_type: 'component',
        unit_name: 'conjure_intake_2',
        subunit_type: 'component',
        subunit_name: 'quiz_question',
        item_type: 'button',
        item_name: `q${cur + 1}_${letter.toLowerCase()}`,
        position_in_unit: cur + 1,
        action_type: 'select',
        action_value: `assessment_${cur + 1}_of_${QS.length}`,
      });
      setAnswers((prev) => ({ ...prev, [`q${cur}`]: letter }));
    },
    [cur, trackContentAction]
  );

  const prevQ = useCallback(() => {
    if (cur > 0) {
      setCur((c) => c - 1);
      window.scrollTo(0, 0);
    }
  }, [cur]);

  const nextQ = useCallback(() => {
    if (!answers[`q${cur}`]) return;
    if (cur < QS.length - 1) {
      setCur((c) => c + 1);
      window.scrollTo(0, 0);
    } else {
      setSection('result');
      window.scrollTo(0, 0);
    }
  }, [cur, answers]);

  // Auto-advance 3s after selection if user doesn't tap next
  useEffect(() => {
    if (section !== 'quiz') return;
    if (!answers[`q${cur}`]) return;
    const t = setTimeout(() => {
      nextQ();
    }, 3000);
    return () => clearTimeout(t);
  }, [answers, cur, section, nextQ]);

  const result = useMemo(() => {
    if (section !== 'result') return null;
    const counts = { A: 0, B: 0, C: 0, D: 0 };
    Object.values(answers).forEach((v) => {
      counts[v]++;
    });
    let type = 'A';
    ['A', 'B', 'C', 'D'].forEach((k) => {
      if (counts[k] > counts[type]) type = k;
    });
    const id = Math.floor(Math.random() * 90000) + 10000;
    return { ...RES[type], id, type };
  }, [section, answers]);

  const handleCtaClick = useCallback(() => {
    trackExternalLink({
      unit_type: 'component',
      unit_name: 'conjure_intake_2',
      subunit_type: 'component',
      subunit_name: 'quiz_result',
      item_type: 'button',
      item_name: 'answer_the_summons',
      target_content_url: appStoreUrl,
    });
  }, [trackExternalLink, appStoreUrl]);

  return (
    <>
      <Head>
        <title>Conjure Assessment Test</title>
      </Head>
      <Script src="https://www.googletagmanager.com/gtag/js?id=G-5YECTVQGXN" strategy="afterInteractive" />
      <Script id="conjure-gtag-intake2" strategy="afterInteractive">{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-5YECTVQGXN');
      `}</Script>

      <div className={styles.page}>
        {/* Header */}
        <div className={styles.hdr}>
          <div className={styles.hdrTop}>
            <span className={styles.hdrLogo}>conjure</span>
            <span className={styles.hdrTagline}>perception assessment test</span>
          </div>
        </div>

        <div className={styles.topbar}>
          seekers online: {seekers !== null ? seekers.toLocaleString() : '—'}
        </div>

        <div className={styles.breadcrumb}>
          conjure<span>›</span>assessments<span>›</span>perception test
        </div>

        {/* Intro */}
        {section === 'intro' && (
          <div className={styles.bodyPad}>
            <img src={`${BASE_PATH}/static/images/conjure/intro-2.png`} alt="" className={styles.introImage} />
            <div className={styles.postingTitle}>open intake, all seekers welcome to join CONJURE</div>
            <div className={styles.postingMeta}>
              <span>posted: {postedHours !== null ? postedHours : '—'} hours ago</span>
              <span>assessment #CAT-004</span>
              <span className={styles.pid}>record id: <span className={styles.redacted}>{recordId !== null ? recordId : '—'}</span></span>
            </div>
            <div className={styles.postBody}>
              <p>
                This assessment measures attunement — the ability to perceive what
                others overlook.<br /> Most people complete it and feel nothing.<br /><br /> A small number find it
                difficult to stop thinking about afterward.<br /><br /> Answer honestly to gain access to Conjure&rsquo;s camera.<br /><br /> We&rsquo;ll know if you
                don&rsquo;t.
              </p>
            </div>
            <div className={styles.sep} />
            <button className={styles.submitBtn} onClick={startQuiz}>
              begin assessment ›
            </button>
            <div className={styles.hiddenMsg}>
              ᔭᓍᑌ ᑕᐊᑎ ᔑᗴᗴ ᗰᓍᖇᗴ ᑦᕼᐊᑎ ᓍᑦᕼᗴᖇᔑ, ᓰᑦ&apos;ᔑ ᐊᒪᖇᗴᐊᗪᔭ ᖴᓍᑌᑎᗪ ᔭᓍᑌ
            </div>
          </div>
        )}

        {/* Quiz */}
        {section === 'quiz' && (
          <div>
            {/* Progress bar */}
            <div className={styles.progWrap}>
              <div className={styles.progRow}>
                <span className={styles.progLbl}>Section {cur + 1}</span>
                <div className={styles.progTrack}>
                  <div className={styles.progFill} style={{ width: `${pct}%` }} />
                </div>
                <span className={styles.progNum}>
                  {cur + 1} / {QS.length}
                </span>
              </div>
            </div>

            <div className={styles.bodyPad}>
              <div className={styles.postingTitle} style={{ fontSize: '15px', marginTop: '6px' }}>
                {q.hed}
              </div>
              <div className={styles.postingMeta}>
                {(q.img || q.grid) && <span className={styles.qTypeLabel}>{q.type}</span>}
                <span className={styles.pid} style={{ marginLeft: '12px' }}>
                  section {cur + 1} of {QS.length}
                </span>
              </div>

              {/* Grid question */}
              {q.grid && GRID_IMAGES[q.grid] && (
                <div className={styles.imgGrid}>
                  {['A', 'B', 'C', 'D'].map((letter) => (
                    <div
                      key={letter}
                      className={`${styles.imgGridCell} ${sel === letter ? styles.sel : ''}`}
                      onClick={() => pick(letter)}
                    >
                      <img src={GRID_IMAGES[q.grid][letter]} alt="" />
                      <div className={styles.cellOverlay} />
                    </div>
                  ))}
                </div>
              )}

              {/* Full-width image question */}
              {!q.grid && q.img && (
                <div className={styles.imgZone}>
                  <img src={q.img} alt="" />
                  <div className={styles.imgScanlines} />
                  <div className={styles.imgVignette} />
                  <span className={styles.imgDate}>{q.date}</span>
                </div>
              )}

              {/* Text options */}
              {(!q.grid || !GRID_IMAGES[q.grid]) && (
                <table className={styles.opts}>
                  <tbody>
                    {q.opts.map((o) => (
                      <tr key={o.l}>
                        <td>
                          <div
                            className={`${styles.optRow} ${sel === o.l ? styles.sel : ''}`}
                            onClick={() => pick(o.l)}
                          >
                            <div className={styles.optLtr}>{o.l}</div>
                            <div className={styles.optTxt}>{o.t}</div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Nav */}
              <div className={styles.navRow}>
                {cur > 0 ? (
                  <button className={styles.backBtn} onClick={prevQ}>
                    ‹ last section
                  </button>
                ) : (
                  <span />
                )}
                <div className={styles.navRight}>
                  <span className={styles.navHint}>
                    {hasAns
                      ? 'response recorded — continue when ready'
                      : 'select a response to continue'}
                  </span>
                  <button
                    className={styles.submitBtn}
                    style={{ width: 'auto', padding: '5px 20px', fontSize: '12px' }}
                    disabled={!hasAns}
                    onClick={nextQ}
                  >
                    {isLast ? 'submit assessment ›' : 'next section ›'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {section === 'result' && result && (
          <div className={styles.bodyPad}>
            <div className={styles.postingTitle}>result transmitted</div>
            <div className={styles.postingMeta}>
              <span>response received</span>
              <span className={styles.pid}>record id: <span className={styles.redacted}>{result.id}</span></span>
            </div>
            <div className={styles.resHed}>{result.h}</div>
            <div className={styles.resMeta}>{result.m}</div>
            <div
              className={styles.resBody}
              dangerouslySetInnerHTML={{ __html: result.b }}
            />
            <pre className={styles.resultArt}>
              {result.art.join('\n')}
            </pre>
            <div className={styles.sep} />
            <a
              className={styles.resCta}
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCtaClick}
            >
              {result.type === 'A'
                ? 'PROCEED WITH CAUTION — DOWNLOAD CONJURE'
                : 'ANSWER THE SUMMONS — DOWNLOAD CONJURE'}
            </a>
            <button
              className={styles.submitBtn}
              style={{ marginTop: '6px' }}
              onClick={retake}
            >
              retake assessment ›
            </button>
            <div className={styles.seekerNote}>
              seeker #<span className={styles.redacted}>{result.id}</span> — registered to the record —{' '}
              {new Date().toLocaleDateString('en-CA')}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          © 2026 conjure &nbsp;|&nbsp; <a href="#">terms</a> &nbsp;|&nbsp;{' '}
          <a href="#">privacy</a>
        </div>
      </div>
    </>
  );
}

export default function Intake2Page() {
  const pageInfo = { context_page_type: 'utility', context_page_id: 'intake2', destination: 'conjure', page_edition: 'en-us' };
  return (
    <TrackingContext.Provider value={pageInfo}>
      <Intake2Quiz />
    </TrackingContext.Provider>
  );
}
